import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../store/reducers/snackbarSlice';
import { AppDispatch } from '../../../store/store';
import {
  PlanDefinition,
  createPlanDefinition,
  deletePlanDefinition,
  editPlanDefinition,
  fetchAllPlanDefinitions,
} from '../planService';

interface Props {
  onClose: () => void;
}

const surface = '#e7ebf2';
const brand = '#0a8081';

// #29: керування ВИЗНАЧЕННЯМИ тарифів (створити/перейменувати/деактивувати).
const PlanDefinitionsModal: React.FC<Props> = ({ onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [plans, setPlans] = useState<PlanDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [editId, setEditId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setPlans(await fetchAllPlanDefinitions());
    } catch {
      dispatch(
        showSnackbar({
          message: 'Не вдалося завантажити тарифи',
          type: 'error',
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const wrap = async (fn: () => Promise<void>, ok: string, fail: string) => {
    setBusy(true);
    try {
      await fn();
      dispatch(showSnackbar({ message: ok, type: 'success' }));
      await load();
    } catch {
      dispatch(showSnackbar({ message: fail, type: 'error' }));
    } finally {
      setBusy(false);
    }
  };

  const handleCreate = () => {
    if (!newName.trim()) return;
    wrap(
      async () => {
        await createPlanDefinition(newName.trim());
        setNewName('');
      },
      'Тариф створено',
      'Не вдалося створити тариф',
    );
  };

  const handleSaveEdit = (p: PlanDefinition) =>
    wrap(
      async () => {
        await editPlanDefinition(p.id, editName.trim() || p.name, p.isActive);
        setEditId(null);
      },
      'Тариф оновлено',
      'Не вдалося оновити тариф',
    );

  const handleToggle = (p: PlanDefinition) =>
    wrap(
      () => editPlanDefinition(p.id, p.name, !p.isActive).then(() => undefined),
      p.isActive ? 'Тариф деактивовано' : 'Тариф активовано',
      'Не вдалося змінити статус',
    );

  const handleDelete = (p: PlanDefinition) =>
    wrap(
      () => deletePlanDefinition(p.id),
      'Тариф деактивовано',
      'Не вдалося видалити тариф',
    );

  const pill = (active: boolean): React.CSSProperties => ({
    padding: '6px 14px',
    borderRadius: 10,
    border: 'none',
    cursor: busy ? 'default' : 'pointer',
    fontSize: 13,
    fontWeight: 600,
    opacity: busy ? 0.6 : 1,
    background: active ? brand : '#e2ecf4',
    color: active ? '#ffffff' : '#152837',
  });

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(21,40,55,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(560px, 92vw)',
          maxHeight: '86vh',
          overflowY: 'auto',
          background: surface,
          borderRadius: 20,
          padding: 24,
          boxShadow: '8px 8px 18px #c5c9cf, -8px -8px 18px #ffffff',
          fontFamily: 'Ubuntu, sans-serif',
          color: '#152837',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 18,
          }}
        >
          <h3 style={{ margin: 0 }}>Тарифи (визначення)</h3>
          <button
            onClick={onClose}
            style={{
              border: 'none',
              background: 'transparent',
              fontSize: 22,
              cursor: 'pointer',
              color: '#596987',
            }}
          >
            ×
          </button>
        </div>

        {/* створення */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Назва нового тарифу"
            style={{
              flex: 1,
              padding: '10px 14px',
              borderRadius: 12,
              border: 'none',
              background: surface,
              boxShadow:
                'inset 4px 4px 8px #c5c9cf, inset -4px -4px 8px #ffffff',
              color: '#152837',
            }}
          />
          <button
            onClick={handleCreate}
            disabled={busy || !newName.trim()}
            style={{ ...pill(true), padding: '10px 18px' }}
          >
            Додати
          </button>
        </div>

        {loading ? (
          <p style={{ color: '#596987' }}>Завантаження…</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {plans.map((p) => (
              <div
                key={p.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 12px',
                  borderRadius: 12,
                  background: surface,
                  boxShadow: '4px 4px 10px #c5c9cf, -4px -4px 10px #ffffff',
                  opacity: p.isActive ? 1 : 0.6,
                }}
              >
                {editId === p.id ? (
                  <input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      borderRadius: 10,
                      border: 'none',
                      background: surface,
                      boxShadow:
                        'inset 3px 3px 6px #c5c9cf, inset -3px -3px 6px #ffffff',
                      color: '#152837',
                    }}
                  />
                ) : (
                  <span style={{ flex: 1, fontWeight: 600 }}>
                    {p.name}
                    {!p.isActive && (
                      <span style={{ color: '#8a9499', fontWeight: 400 }}>
                        {' '}
                        (неактивний)
                      </span>
                    )}
                  </span>
                )}

                {editId === p.id ? (
                  <button
                    onClick={() => handleSaveEdit(p)}
                    disabled={busy}
                    style={pill(true)}
                  >
                    Зберегти
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setEditId(p.id);
                      setEditName(p.name);
                    }}
                    disabled={busy}
                    style={pill(false)}
                  >
                    Перейм.
                  </button>
                )}
                <button
                  onClick={() => handleToggle(p)}
                  disabled={busy}
                  style={pill(p.isActive)}
                >
                  {p.isActive ? 'Активний' : 'Активувати'}
                </button>
                {p.isActive && (
                  <button
                    onClick={() => handleDelete(p)}
                    disabled={busy}
                    style={{ ...pill(false), color: '#b3261e' }}
                  >
                    Видалити
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanDefinitionsModal;
